import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Shield,
  Heart,
  AlertTriangle,
  Phone,
  Edit,
  Search,
  Loader2,
  Plus,
  X,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface EmergencyNumbers {
  police?: string;
  ambulance?: string;
  fire?: string;
  general?: string;
}

interface CountryRiskProfile {
  id: string;
  name: string;
  country_code: string | null;
  risk_level: string | null;
  emergency_numbers: EmergencyNumbers | null;
  health_notes: string | null;
  security_notes: string | null;
  recommended_actions: string[] | null;
}

const CountryRiskProfileTab = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState<CountryRiskProfile[]>([]);
  const [filteredCountries, setFilteredCountries] = useState<CountryRiskProfile[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingCountry, setEditingCountry] = useState<CountryRiskProfile | null>(null);
  const [formData, setFormData] = useState({
    country_code: "",
    risk_level: "low",
    emergency_numbers: { police: "", ambulance: "", fire: "", general: "" },
    health_notes: "",
    security_notes: "",
    recommended_actions: [""],
  });

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      setFilteredCountries(
        countries.filter((c) =>
          c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.country_code?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    } else {
      setFilteredCountries(countries);
    }
  }, [searchTerm, countries]);

  const fetchCountries = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("countries")
      .select("id, name, country_code, risk_level, emergency_numbers, health_notes, security_notes, recommended_actions")
      .order("name");

    if (error) {
      toast({ variant: "destructive", title: "Error", description: error.message });
    } else {
      // Cast the Json type to our expected structure
      const typedData = (data || []).map((c) => ({
        ...c,
        emergency_numbers: c.emergency_numbers as EmergencyNumbers | null,
      }));
      setCountries(typedData);
    }
    setLoading(false);
  };

  const handleEdit = (country: CountryRiskProfile) => {
    setEditingCountry(country);
    const emergencyNumbers = country.emergency_numbers || {};
    setFormData({
      country_code: country.country_code || "",
      risk_level: country.risk_level || "low",
      emergency_numbers: {
        police: (emergencyNumbers as any).police || "",
        ambulance: (emergencyNumbers as any).ambulance || "",
        fire: (emergencyNumbers as any).fire || "",
        general: (emergencyNumbers as any).general || "",
      },
      health_notes: country.health_notes || "",
      security_notes: country.security_notes || "",
      recommended_actions: country.recommended_actions?.length ? country.recommended_actions : [""],
    });
    setIsEditOpen(true);
  };

  const handleSave = async () => {
    if (!editingCountry) return;

    const cleanedActions = formData.recommended_actions.filter((a) => a.trim() !== "");
    const cleanedEmergency = Object.fromEntries(
      Object.entries(formData.emergency_numbers).filter(([_, v]) => v.trim() !== "")
    );

    const { error } = await supabase
      .from("countries")
      .update({
        country_code: formData.country_code || null,
        risk_level: formData.risk_level,
        emergency_numbers: Object.keys(cleanedEmergency).length > 0 ? cleanedEmergency : null,
        health_notes: formData.health_notes || null,
        security_notes: formData.security_notes || null,
        recommended_actions: cleanedActions.length > 0 ? cleanedActions : null,
      })
      .eq("id", editingCountry.id);

    if (error) {
      toast({ variant: "destructive", title: "Error", description: error.message });
    } else {
      toast({ title: "Success", description: "Country risk profile updated" });
      setIsEditOpen(false);
      fetchCountries();
    }
  };

  const addAction = () => {
    setFormData({ ...formData, recommended_actions: [...formData.recommended_actions, ""] });
  };

  const removeAction = (index: number) => {
    const newActions = formData.recommended_actions.filter((_, i) => i !== index);
    setFormData({ ...formData, recommended_actions: newActions.length ? newActions : [""] });
  };

  const updateAction = (index: number, value: string) => {
    const newActions = [...formData.recommended_actions];
    newActions[index] = value;
    setFormData({ ...formData, recommended_actions: newActions });
  };

  const getRiskBadge = (level: string | null) => {
    switch (level) {
      case "high":
        return <Badge className="bg-red-100 text-red-700 border-red-200">High</Badge>;
      case "medium":
        return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Medium</Badge>;
      default:
        return <Badge className="bg-green-100 text-green-700 border-green-200">Low</Badge>;
    }
  };

  const hasEmergencyNumbers = (country: CountryRiskProfile) => {
    const nums = country.emergency_numbers;
    if (!nums) return false;
    return Object.values(nums).some((v) => v && String(v).trim() !== "");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Country Risk Profiles
          </CardTitle>
          <CardDescription>
            Manage emergency numbers, health notes, and security information for each country
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search countries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Badge variant="outline">
              {filteredCountries.filter(hasEmergencyNumbers).length} / {filteredCountries.length} configured
            </Badge>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Country</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Risk Level</TableHead>
                  <TableHead>Emergency #</TableHead>
                  <TableHead>Health</TableHead>
                  <TableHead>Security</TableHead>
                  <TableHead className="w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCountries.map((country) => (
                  <TableRow key={country.id}>
                    <TableCell className="font-medium">{country.name}</TableCell>
                    <TableCell>{country.country_code || "-"}</TableCell>
                    <TableCell>{getRiskBadge(country.risk_level)}</TableCell>
                    <TableCell>
                      {hasEmergencyNumbers(country) ? (
                        <Phone className="h-4 w-4 text-green-600" />
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {country.health_notes ? (
                        <Heart className="h-4 w-4 text-pink-500" />
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {country.security_notes ? (
                        <AlertTriangle className="h-4 w-4 text-orange-500" />
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(country)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Risk Profile: {editingCountry?.name}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Country Code (ISO)</Label>
                <Input
                  value={formData.country_code}
                  onChange={(e) => setFormData({ ...formData, country_code: e.target.value.toUpperCase() })}
                  placeholder="FR, ES, PT..."
                  maxLength={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Risk Level</Label>
                <Select
                  value={formData.risk_level}
                  onValueChange={(v) => setFormData({ ...formData, risk_level: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Emergency Numbers
              </Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Police</Label>
                  <Input
                    value={formData.emergency_numbers.police}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        emergency_numbers: { ...formData.emergency_numbers, police: e.target.value },
                      })
                    }
                    placeholder="17"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Ambulance</Label>
                  <Input
                    value={formData.emergency_numbers.ambulance}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        emergency_numbers: { ...formData.emergency_numbers, ambulance: e.target.value },
                      })
                    }
                    placeholder="15"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Fire</Label>
                  <Input
                    value={formData.emergency_numbers.fire}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        emergency_numbers: { ...formData.emergency_numbers, fire: e.target.value },
                      })
                    }
                    placeholder="18"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">General Emergency</Label>
                  <Input
                    value={formData.emergency_numbers.general}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        emergency_numbers: { ...formData.emergency_numbers, general: e.target.value },
                      })
                    }
                    placeholder="112"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Heart className="h-4 w-4" />
                Health Notes
              </Label>
              <Textarea
                value={formData.health_notes}
                onChange={(e) => setFormData({ ...formData, health_notes: e.target.value })}
                placeholder="Required vaccinations, health risks, healthcare quality..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Security Notes
              </Label>
              <Textarea
                value={formData.security_notes}
                onChange={(e) => setFormData({ ...formData, security_notes: e.target.value })}
                placeholder="Safety concerns, areas to avoid, general security advice..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center justify-between">
                <span>Recommended Actions</span>
                <Button variant="ghost" size="sm" onClick={addAction}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </Label>
              <div className="space-y-2">
                {formData.recommended_actions.map((action, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input
                      value={action}
                      onChange={(e) => updateAction(idx, e.target.value)}
                      placeholder="e.g., Register with embassy"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeAction(idx)}
                      disabled={formData.recommended_actions.length === 1 && action === ""}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsEditOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CountryRiskProfileTab;
